import { authHeaders } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Captures the 3D preview canvas and composites dimension labels on top.
 * Uses DPR-aware scaling for proper Retina/HiDPI support.
 */
export async function capturePreviewSnapshot() {
  try {
    const canvas = document.querySelector('canvas');
    if (!canvas) return null;

    const container = canvas.closest('[data-testid$="-preview"]')
      || canvas.closest('[data-testid$="-3d-preview"]')
      || canvas.parentElement?.parentElement;
    if (!container) return null;

    // Reset camera to saved fitted position
    if (canvas.__orbitControls) {
      canvas.__orbitControls.reset();
    }

    // Force multiple render frames so the scene + label positions settle
    if (canvas.__renderFrame) canvas.__renderFrame();
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
    if (canvas.__renderFrame) canvas.__renderFrame();
    await new Promise(r => setTimeout(r, 400));
    if (canvas.__renderFrame) canvas.__renderFrame();
    // One final frame to guarantee labels are positioned after camera settle
    await new Promise(r => requestAnimationFrame(r));

    // Capture the 3D scene from the WebGL canvas
    const canvasDataUrl = canvas.toDataURL('image/png');
    const sceneImg = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = canvasDataUrl;
    });

    const containerRect = container.getBoundingClientRect();
    const canvasRect = canvas.getBoundingClientRect();

    // Use the actual DPR for the output scale, clamped to 2-3x for sanity
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const scale = Math.max(dpr, 2);

    const offscreen = document.createElement('canvas');
    offscreen.width = Math.round(containerRect.width * scale);
    offscreen.height = Math.round(containerRect.height * scale);
    const ctx = offscreen.getContext('2d');
    ctx.scale(scale, scale);

    // 1. Gradient background matching the live preview
    const grad = ctx.createLinearGradient(0, 0, containerRect.width, containerRect.height);
    grad.addColorStop(0, '#f5f0eb');
    grad.addColorStop(1, '#e8e0d8');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, containerRect.width, containerRect.height);

    // 2. Draw the 3D scene (map canvas position within container)
    const cx = canvasRect.left - containerRect.left;
    const cy = canvasRect.top - containerRect.top;
    ctx.drawImage(sceneImg, cx, cy, canvasRect.width, canvasRect.height);

    // 3. Find and draw dimension labels
    const labels = container.querySelectorAll('[data-testid^="dim-label-"]');
    labels.forEach(label => {
      const text = label.textContent || '';
      if (!text) return;

      // Skip labels that are invisible or not positioned yet
      const computedStyle = window.getComputedStyle(label);
      if (computedStyle.opacity === '0' || computedStyle.display === 'none') return;

      const labelRect = label.getBoundingClientRect();
      const lx = labelRect.left - containerRect.left;
      const ly = labelRect.top - containerRect.top;

      // Clamp label position within container bounds
      const clampedLx = Math.max(0, Math.min(lx, containerRect.width - 20));
      const clampedLy = Math.max(0, Math.min(ly, containerRect.height - 16));

      const fontSize = parseFloat(computedStyle.fontSize) || 12;
      const bgColor = computedStyle.backgroundColor || 'rgba(255,255,255,0.9)';

      // Pill background
      const padding = { x: 8, y: 4 };
      ctx.font = `${computedStyle.fontWeight || '600'} ${fontSize}px ${computedStyle.fontFamily || 'system-ui, sans-serif'}`;
      const textWidth = ctx.measureText(text).width;
      const pillW = textWidth + padding.x * 2;
      const pillH = fontSize + padding.y * 2;
      const r = pillH / 2;

      ctx.fillStyle = bgColor !== 'rgba(0, 0, 0, 0)' ? bgColor : 'rgba(255,255,255,0.92)';
      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(clampedLx, clampedLy, pillW, pillH, r);
      } else {
        ctx.moveTo(clampedLx + r, clampedLy);
        ctx.arcTo(clampedLx + pillW, clampedLy, clampedLx + pillW, clampedLy + pillH, r);
        ctx.arcTo(clampedLx + pillW, clampedLy + pillH, clampedLx, clampedLy + pillH, r);
        ctx.arcTo(clampedLx, clampedLy + pillH, clampedLx, clampedLy, r);
        ctx.arcTo(clampedLx, clampedLy, clampedLx + pillW, clampedLy, r);
        ctx.closePath();
      }
      ctx.fill();

      ctx.strokeStyle = 'rgba(0,0,0,0.12)';
      ctx.lineWidth = 0.5;
      ctx.stroke();

      // Text
      ctx.fillStyle = computedStyle.color || '#374151';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, clampedLx + padding.x, clampedLy + pillH / 2);
    });

    // Upload
    const blob = await new Promise(resolve => offscreen.toBlob(resolve, 'image/png'));
    if (!blob) return null;

    const formData = new FormData();
    formData.append('file', blob, 'preview.png');

    const uploadRes = await fetch(`${API}/api/preview-snapshot`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });

    if (uploadRes.ok) {
      const data = await uploadRes.json();
      return data.url;
    }
    return null;
  } catch (e) {
    console.warn('Preview snapshot capture failed:', e);
    return null;
  }
}
